<template>
    <div class="tinymce-editor">
        <textarea :id="editorId"></textarea>
    </div>
</template>

<script>
// 动态加载 TinyMCE - 使用多个 CDN 源提高可用性
const loadTinymce = () => {
    return new Promise((resolve, reject) => {
        if (window.tinymce) {
            console.log('TinyMCE already loaded')
            resolve(window.tinymce)
            return
        }
        
        // CDN 源列表（按优先级）
        const cdnSources = [
            'https://cdn.jsdelivr.net/npm/tinymce@6.8.2/tinymce.min.js',
            'https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js',
            'https://unpkg.com/tinymce@6.8.2/tinymce.min.js'
        ]
        
        let currentSourceIndex = 0
        
        const loadFromSource = (index) => {
            if (index >= cdnSources.length) {
                reject(new Error('All CDN sources failed'))
                return
            }
            
            const script = document.createElement('script')
            script.src = cdnSources[index]
            script.onload = () => {
                console.log(`TinyMCE core loaded from source ${index + 1}`)
                resolve(window.tinymce)
            }
            script.onerror = () => {
                console.warn(`CDN source ${index + 1} failed:`, cdnSources[index])
                loadFromSource(index + 1)
            }
            document.head.appendChild(script)
        }
        
        console.log('Loading TinyMCE from CDN...')
        loadFromSource(0)
    })
}

export default {
    name: 'TinymceEditor',
    props: {
        value: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        height: {
            type: Number,
            default: null // 默认为 null，表示自适应
        },
        autoHeight: {
            type: Boolean,
            default: true // 默认启用自动高度
        },
        menubar: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: '请输入内容...'
        },
        typeWriter: {
            type: Boolean,
            default: false // 是否启用逐字打印效果
        },
        typeWriterSpeed: {
            type: Number,
            default: 50 // 逐字打印速度（毫秒/字）
        },
        contentId: {
            type: String,
            default: '' // 内容 ID，用于判断是否是新内容
        }
    },
    data() {
        return {
            editor: null,
            editorId: `tinymce-${Math.random().toString(36).substr(2, 9)}`,
            typeWriterTimer: null,
            isTypeWriting: false
        }
    },
    watch: {
        value(newVal) {
            if (this.editor && newVal !== this.editor.getContent()) {
                // 只有在非逐字打印状态下才直接设置内容
                if (!this.isTypeWriting && this.editor.initialized) {
                    this.editor.setContent(newVal)
                }
            }
        },
        contentId(newVal) {
            // 监听 contentId 变化，只有当 contentId 存在且编辑器已就绪时才执行逐字打印
            if (newVal && this.editor && this.typeWriter && !this.isTypeWriting && this.editor.initialized) {
                this.typeWriterEffect(this.value)
            }
        },
        disabled(newVal) {
            if (this.editor) {
                this.editor.setMode(newVal ? 'readonly' : 'design')
            }
        }
    },
    mounted() {
        this.initEditor()
    },
    beforeDestroy() {
        this.destroy()
        if (this.typeWriterTimer) {
            clearInterval(this.typeWriterTimer)
            this.typeWriterTimer = null
        }
    },
    methods: {
        // 获取父元素高度
        getParentHeight() {
            const parent = this.$el.parentElement
            if (!parent) {
                return this.height || 400
            }
            
            const parentHeight = parent.clientHeight
            
            // 如果父元素没有明确的高度，使用默认值
            if (!parentHeight || parentHeight === 0) {
                return this.height || 400
            }
            
            return parentHeight
        },
        async initEditor() {
            try {
                console.log('Initializing TinyMCE...')
                const tinymce = await loadTinymce()
                
                // 如果编辑器已存在，先销毁
                if (this.editor) {
                    this.destroy()
                }
                
                // 计算编辑器高度
                let editorHeight = this.height
                
                // 如果启用了自动高度且没有指定固定高度
                if (this.autoHeight && !this.height) {
                    // 等待 DOM 渲染完成
                    this.$nextTick(() => {
                        editorHeight = this.getParentHeight()
                        console.log('Auto height calculated:', editorHeight)
                    })
                }
                
                tinymce.init({
                    selector: `#${this.editorId}`,
                    height: editorHeight,
                    menubar: this.menubar ? true : false,
                    placeholder: this.placeholder,
                    language: 'zh_CN',
                    language_url: '/tinymce/langs/zh_CN.min.js',
                    skin_url: false,
                    content_css: false,
                    plugins: [
                        'lists link image table code fullscreen preview searchreplace autosave visualblocks wordcount'
                    ],
                    toolbar: [
                        'undo redo | formatselect | bold italic backcolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | removeformat | image table | code fullscreen preview'
                    ],
                    setup: (editor) => {
                        this.editor = editor
                        
                        editor.on('init', () => {
                            console.log('TinyMCE initialized')
                            editor.initialized = true
                            if (this.value) {
                                // 如果启用逐字打印且有 contentId，使用逐字效果
                                if (this.typeWriter && this.contentId) {
                                    this.typeWriterEffect(this.value)
                                } else {
                                    editor.setContent(this.value)
                                }
                            }
                        })
                        
                        editor.on('change', () => {
                            this.$emit('input', editor.getContent())
                        })
                        
                        editor.on('blur', () => {
                            this.$emit('blur', editor)
                        })
                        
                        editor.on('focus', () => {
                            this.$emit('focus', editor)
                        })
                    },
                    init_instance_callback: (editor) => {
                        console.log('TinyMCE instance created:', editor.id)
                    }
                })
            } catch (error) {
                console.error('TinyMCE initialization failed:', error)
                console.error('Error details:', error.message, error.stack)
            }
        },
        destroy() {
            if (this.editor) {
                console.log('Destroying TinyMCE instance:', this.editor.id)
                this.editor.remove()
                this.editor = null
            }
        },
        // 逐字打印效果（保留富文本格式）
        typeWriterEffect(content) {
            // 清除之前的定时器
            if (this.typeWriterTimer) {
                clearInterval(this.typeWriterTimer)
                this.typeWriterTimer = null
            }
            
            // 解析 HTML 结构并转换为字符数组
            const chunks = this.parseHtmlToChunks(content)
            
            let currentHtml = ''
            let currentIndex = 0
            this.isTypeWriting = true
            
            // 清空编辑器
            this.editor.setContent('')
            
            // 逐字显示
            this.typeWriterTimer = setInterval(() => {
                if (currentIndex < chunks.length) {
                    currentHtml += chunks[currentIndex]
                    
                    // 尝试闭合未完成的标签
                    const completeHtml = this.closeIncompleteTags(currentHtml)
                    this.editor.setContent(completeHtml)
                    
                    // 滚动到底部 - 使用多种方式确保生效
                    this.$nextTick(() => {
                        this.scrollToBottom()
                    })
                    
                    currentIndex++
                } else {
                    // 逐字打印完成
                    clearInterval(this.typeWriterTimer)
                    this.typeWriterTimer = null
                    this.isTypeWriting = false
                    
                    // 确保使用完整的 HTML
                    this.editor.setContent(content)
                    
                    // 最后再滚动一次
                    this.$nextTick(() => {
                        this.scrollToBottom()
                    })
                    
                    console.log('Type writer effect completed with formatting preserved')
                }
            }, this.typeWriterSpeed)
        },
        
        // 滚动到底部
        scrollToBottom() {
            try {
                // 获取编辑器主体
                const editorBody = this.editor.getBody()
                if (!editorBody) return
                
                // 方法 1: 设置 scrollTop
                editorBody.scrollTop = editorBody.scrollHeight
                
                // 方法 2: 使用 scrollIntoView
                if (editorBody.lastChild) {
                    editorBody.lastChild.scrollIntoView({ 
                        behavior: 'auto',
                        block: 'end'
                    })
                }
                
                // 方法 3: 同时滚动文档区域（针对某些浏览器）
                const doc = this.editor.getDoc()
                if (doc && doc.documentElement) {
                    doc.documentElement.scrollTop = doc.documentElement.scrollHeight
                }
                
                // 方法 4: 滚动 iframe 窗口
                const win = this.editor.getWin()
                if (win && win.scrollTo) {
                    win.scrollTo(0, doc.body.scrollHeight)
                }
            } catch (error) {
                console.warn('Scroll to bottom failed:', error)
            }
        },
        
        // 解析 HTML 为字符/标签片段数组
        parseHtmlToChunks(content) {
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = content
            const chunks = []
            
            const processNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    // 文本节点 - 逐字添加
                    const text = node.textContent
                    for (let i = 0; i < text.length; i++) {
                        chunks.push(text[i])
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    // 元素节点 - 完整添加标签
                    let tagStart = `<${node.nodeName.toLowerCase()}`
                    
                    // 添加属性
                    Array.from(node.attributes).forEach(attr => {
                        tagStart += ` ${attr.name}="${attr.value}"`
                    })
                    
                    tagStart += '>'
                    chunks.push(tagStart)
                    
                    // 递归处理子节点
                    Array.from(node.childNodes).forEach(child => {
                        processNode(child)
                    })
                    
                    // 添加结束标签
                    chunks.push(`</${node.nodeName.toLowerCase()}>`)
                }
            }
            
            Array.from(tempDiv.childNodes).forEach(child => {
                processNode(child)
            })
            
            return chunks
        },
        
        // 闭合未完成的 HTML 标签
        closeIncompleteTags(html) {
            const tagStack = []
            const tagRegex = /<(\/?)([a-z][a-z0-9]*)\b[^>]*>/gi
            const selfClosingTags = ['br', 'hr', 'img', 'input', 'meta', 'link']
            
            let match
            while ((match = tagRegex.exec(html)) !== null) {
                const isClosing = match[1] === '/'
                const tagName = match[2].toLowerCase()
                
                if (selfClosingTags.includes(tagName)) {
                    continue
                }
                
                if (isClosing) {
                    // 闭合标签 - 弹出栈
                    const lastTag = tagStack.pop()
                    if (lastTag !== tagName) {
                        // 标签不匹配，需要修复
                        console.warn('Tag mismatch detected')
                    }
                } else {
                    // 开始标签 - 压入栈
                    tagStack.push(tagName)
                }
            }
            
            // 添加缺失的闭合标签
            let completeHtml = html
            while (tagStack.length > 0) {
                const tag = tagStack.pop()
                completeHtml += `</${tag}>`
            }
            
            return completeHtml
        }
    }
}
</script>

<style lang="less" scoped>
.tinymce-editor {
    width: 100%;
    height: 100%;
    min-height: 200px; // 最小高度
    
    ::v-deep(.tox-tinymce) {
        border-radius: 4px;
        border: 1px solid #E4E7ED !important;
        height: 100% !important;
        display: flex;
        flex-direction: column;
        
        &:hover {
            border-color: #00BF7F !important;
        }
    }
    
    ::v-deep(.tox-toolbar-overlord) {
        border-bottom: 1px solid #E4E7ED !important;
        flex-shrink: 0; // 工具栏不收缩
    }
    
    ::v-deep(.tox-edit-area) {
        flex: 1; // 编辑区域占满剩余空间
        overflow: auto; // 内容过多时显示滚动条
    }
}
</style>
